import { Component, computed, input, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ContactPoint, ContactPointType } from '../../../api/app-types/contact-point.app';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatDivider } from '@angular/material/divider';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
	selector: 'lvr-send-email-button',
	imports: [TranslocoPipe, MatIcon, MatButtonModule, MatTooltip, MatMenuModule, MatDivider],
	templateUrl: './send-email-button.html',
	styleUrl: './send-email-button.scss',
})
export class SendEmailButton {
	@ViewChild(MatMenuTrigger) private menuTrigger?: MatMenuTrigger;

	public readonly participantName = input<string>('');
	public readonly subject = input<string>('');
	public readonly body = input<string>('');
	public readonly contactPoints = input<ContactPoint[]>([]);

	public readonly emails = computed(() =>
		(this.contactPoints() ?? [])
			.filter((cp) => cp.categoryContactPointId === ContactPointType.EMAIL)
			.map((cp) => ({ id: cp.id, value: cp.contactValue, remark: cp.remark, preferred: !!cp.preferredWayOfContact })),
	);
	public readonly preferredEmails = computed(() => this.emails().filter((e) => e.preferred));
	public readonly hasPreferred = computed(() => this.emails().some((e) => e.preferred));
	public readonly disabled = computed(() => this.emails().length === 0);
	public readonly showMenu = computed(() => {
		const emails = this.emails();
		const preferredCount = emails.filter((e) => e.preferred).length;
		// Show menu if:
		// - Multiple emails and no preferred at all, OR
		// - Multiple emails and multiple preferred
		return emails.length > 1 && preferredCount !== 1;
	});

	public getButtonTooltip() {
		if (this.disabled()) return 'send_email.no_email';
		return 'send_email.send_to';
	}

	public onClick() {
		if (this.disabled()) return;

		const list = this.emails();
		const preferredList = list.filter((e) => e.preferred);

		// Participant has a preferred email
		if (preferredList.length === 1) {
			this.openMail(preferredList[0].value ?? '');
			return;
		}

		// Participant has only one email
		if (list.length === 1) {
			this.openMail(list[0].value ?? '');
			return;
		}

		// Participant has more than one email or more than one preferred
		this.menuTrigger?.openMenu();
	}

	public selectAndSend(email: string) {
		this.menuTrigger?.closeMenu();
		this.openMail(email);
	}

	private openMail(recipient: string) {
		const href = this.buildMailto(recipient, this.subject(), this.body());
		window.location.href = href;
	}

	/**
	 * Builds a safe mailto U with properly encoded subject and body.
	 * Encodes special characters to prevent URL breakage (spaces, &, ?, etc.)
	 */
	private buildMailto(recipient: string, subject: string, body: string): string {
		const encoded = (s: string) => encodeURIComponent(s ?? '');
		const to = encodeURIComponent(recipient);
		return `mailto:${to}?subject=${encoded(subject)}&body=${encoded(body)}`;
	}
}
